import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, GraduationCap, Briefcase, MessageSquare, ShieldCheck, BookOpen, Calendar, TrendingUp, ArrowLeft, UserCog, MessagesSquare } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import ProfawzLogo from "./ProfawzLogo";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const canGoBack = user && location.pathname !== "/dashboard" && location.pathname !== "/";
    const [unread, setUnread] = useState(0);

    const isAlternant = user?.role === "alternant";
    const isFormateur = user?.role === "formateur";
    const isResponsable = user?.role === "responsable";
    const isAdmin = user?.role === "admin";

    // Poll unread count every 20s for non-admin users
    useEffect(() => {
        if (!user || isAdmin) { setUnread(0); return; }
        let alive = true;
        const tick = async () => {
            try {
                const { data } = await api.get("/messages/unread-count");
                if (alive) setUnread(data?.unread || 0);
            } catch { /* ignore */ }
        };
        tick();
        const t = setInterval(tick, 20000);
        return () => { alive = false; clearInterval(t); };
    }, [user, isAdmin]);

    return (
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-rule">
            <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
                <div className="flex items-center gap-3">
                    {canGoBack && (
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="p-2 rounded-sm hover:bg-ink/5 text-muted2 hover:text-ink transition-colors"
                            aria-label="Retour"
                            title="Retour à la page précédente"
                            data-testid="navbar-back-button"
                        >
                            <ArrowLeft size={18} />
                        </button>
                    )}
                    <Link to={user ? (isAdmin ? "/admin" : "/dashboard") : "/"} className="flex items-center gap-3" data-testid="navbar-logo">
                        <ProfawzLogo size={32} />
                        <div className="leading-tight">
                            <div className="h-display text-lg font-bold tracking-tight">Profawz</div>
                            <div className="text-[10px] font-mono text-muted2 uppercase tracking-wider">
                                {isAdmin ? "Console administrateur" : "Suivre ta progression, réussir ton BTS"}
                            </div>
                        </div>
                    </Link>
                </div>

                <nav className="flex items-center gap-3">
                    {user ? (
                        <>
                            {/* Role badges */}
                            {isResponsable && (
                                <span className="pill bg-orange text-white inline-flex items-center" data-testid="nav-role-responsable">
                                    <ShieldCheck size={11} className="mr-1" /> Responsable
                                </span>
                            )}
                            {isFormateur && (
                                <span className="pill bg-ink text-white inline-flex items-center" data-testid="nav-role-formateur">
                                    <Briefcase size={11} className="mr-1" /> Formateur
                                </span>
                            )}
                            {isAlternant && (
                                <span className="pill bg-orange-50 text-orange-700 border border-orange-200 inline-flex items-center" data-testid="nav-role-alternant">
                                    <GraduationCap size={11} className="mr-1" /> Alternant
                                </span>
                            )}
                            {isAdmin && (
                                <span className="pill bg-amber-100 text-amber-900 border border-amber-300 inline-flex items-center" data-testid="nav-role-admin">
                                    <ShieldCheck size={11} className="mr-1" /> Admin
                                </span>
                            )}

                            {/* Subscription pills removed — Profawz is now invitation-only.
                                The previous Stripe trial/active/cancelled chips are no longer relevant. */}
                            {isResponsable && (
                                <span className="pill bg-emerald-600 text-white hidden sm:inline-flex" data-testid="nav-free-mode-badge" title="Profawz · accès activé par l'équipe">
                                    Accès activé
                                </span>
                            )}

                            {/* ADMIN — read-only observatory: full visibility, no creation */}
                            {isAdmin && (
                                <>
                                    <Link to="/admin" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-orange hover:text-orange/80" data-testid="nav-admin-link">
                                        <ShieldCheck size={14} /> Console
                                    </Link>
                                    <Link to="/cours" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-admin-courses-link">
                                        <BookOpen size={14} /> Évaluations
                                    </Link>
                                    <Link to="/agenda" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-admin-agenda-link">
                                        <Calendar size={14} /> Agenda
                                    </Link>
                                    <Link to="/bulletins" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-admin-bulletins-link">
                                        <Briefcase size={14} /> Bulletins
                                    </Link>
                                    <Link to="/avis" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-admin-reviews-link">
                                        <MessageSquare size={14} /> Avis
                                    </Link>
                                </>
                            )}

                            {/* Email (only for non-admin) */}
                            {!isAdmin && (
                                <span className="hidden md:block text-xs text-muted2 font-mono" data-testid="nav-user-email">{user.email}</span>
                            )}

                            {/* FORMATEUR ONLY — Cours/TPs + accès Étudiants */}
                            {isFormateur && (
                                <>
                                    <Link to="/etudiants" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-students-link-formateur">
                                        <Briefcase size={14} /> Étudiants
                                    </Link>
                                    <Link to="/cours" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-courses-link">
                                        <BookOpen size={14} /> Évaluations
                                    </Link>
                                </>
                            )}

                            {/* ALTERNANT ONLY — Progression */}
                            {isAlternant && (
                                <Link to={`/progression/${user.id}`} className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-progression-link">
                                    <TrendingUp size={14} /> Progression
                                </Link>
                            )}

                            {/* RESPONSABLE — Agenda + Bulletins (no Cours, replaced by Messages) */}
                            {isResponsable && (
                                <>
                                    <Link to="/etudiants" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-students-link">
                                        <Briefcase size={14} /> Étudiants
                                    </Link>
                                    <Link to="/promotions" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-promotions-link">
                                        <BookOpen size={14} /> Promotions
                                    </Link>
                                    <Link to="/formateurs" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-formateurs-link">
                                        <Briefcase size={14} /> Formateurs
                                    </Link>
                                    <Link to="/cours" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-resp-cours-link">
                                        <BookOpen size={14} /> Évaluations
                                    </Link>
                                    <Link to="/agenda" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-agenda-link">
                                        <Calendar size={14} /> Agenda
                                    </Link>
                                    <Link to="/bulletins" className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-bulletins-link">
                                        <Briefcase size={14} /> Bulletins
                                    </Link>
                                </>
                            )}

                            {/* MESSAGES — for all non-admin users */}
                            {!isAdmin && (
                                <Link to="/messages" className="relative inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-messages-link">
                                    <MessagesSquare size={14} />
                                    <span className="hidden sm:inline">Messages</span>
                                    {unread > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-orange text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="nav-messages-unread">
                                            {unread > 9 ? "9+" : unread}
                                        </span>
                                    )}
                                </Link>
                            )}

                            {/* AVIS — non-admin */}
                            {!isAdmin && (
                                <Link to="/avis" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-reviews-link">
                                    <MessageSquare size={14} /> Avis
                                </Link>
                            )}

                            {/* MON COMPTE — for all */}
                            <Link to="/account" className="inline-flex items-center gap-1.5 text-xs font-medium text-ink/70 hover:text-orange transition-colors" data-testid="nav-account-link" title="Mon profil">
                                <UserCog size={14} /> <span className="hidden sm:inline">Mon compte</span>
                            </Link>

                            <button onClick={async () => { await logout(); navigate("/login"); }} className="btn-outline !px-3 !py-2 text-xs" data-testid="nav-logout-button">
                                <LogOut size={14} />
                                <span className="hidden sm:inline">Déconnexion</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/tuteur" className="text-sm font-medium hover:text-orange transition-colors" data-testid="nav-public-tutor">Espace tuteur</Link>
                            <Link to="/login" className="text-sm font-medium hover:text-orange transition-colors" data-testid="nav-login-link">Se connecter</Link>
                            <Link to="/register" className="btn-primary !px-4 !py-2 text-sm" data-testid="nav-register-link">Créer un compte</Link>
                        </>
                    )}
                </nav>
            </div>
        </header>
    );
}
